import { addDays, format, parse } from 'date-fns';
import { Form as FormikForm, Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import React, { useEffect, useState } from 'react';
import { Col, Row, Spinner, Stack } from 'react-bootstrap';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import { BackButton } from '../../../../../components/buttons/BackButton';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import Stepper from '../../../../../components/stepper/Stepper';
import { useToast } from '../../../../../context';
import { locationsApi } from '../../../../../services/endpoints/places/location';
import { useGetVenuesQuery } from '../../../../../services/endpoints/places/venue';
import {
  useCreateCourseMutation,
  useGetCourseQuery,
  useUpdateCourseMutation
} from '../../../../../services/endpoints/schedule/course';
import { getCurrentUser } from '../../../../../services/helper';
import { useAppDispatch } from '../../../../../services/hooks';
import { Venue } from '../../../../../types/venue.type';
import {
  hhmmToAmPm,
  isValidISODate,
  timeStringToMinutes,
  toDateOnly
} from '../../../../../utils/dateHelpers';
import {
  dateFromString,
  formatDate,
  formHhmmToStoredSessionAmPm,
  isMissingSessionTime,
  sessionTimeToFormHhmm
} from '../../../../../utils/dateUtils';
import {
  convertDaysToFull,
  FORM_DAY_ORDER,
  getErrorMessage,
  parseStoredDayOfWeek
} from '../../../../../utils/utils';
import { FadeIn } from '../../../../animations/FadeIn';
import { AdditionalInfoForm } from './NewSessionSteps/AdditionalInfoForm';
import { ClassesForm } from './NewSessionSteps/ClassesForm';
import { DocumentsAndFilesForm } from './NewSessionSteps/DocumentsAndFilesForm';
import { InstructorDetailsForm } from './NewSessionSteps/InstructorDetailsForm';
import { ReviewStep } from './NewSessionSteps/ReviewStep';
import { ScheduleForm } from './NewSessionSteps/ScheduleForm';
import { SessionDetailsForm } from './NewSessionSteps/SessionDetailsForm';
import { VenueForm } from './NewSessionSteps/VenueForm';
import { userCanEditCreateDelete_Sessions } from './SessionsView';

const steps = [
  { name: 'Session Details', form: SessionDetailsForm },
  { name: 'Venue', form: VenueForm },
  { name: 'Instructor', form: InstructorDetailsForm },
  { name: 'Additional Info', form: AdditionalInfoForm },
  { name: 'Schedule', form: ScheduleForm },
  { name: 'Classes', form: ClassesForm },
  { name: 'Documents & Files', form: DocumentsAndFilesForm },
  { name: 'Review', form: ReviewStep }
];

interface ClassValues {
  id?: number;
  date: string;
  starts_at: string;
  ends_at: string;
  notes?: string;
  lesson_plan_ids?: { id: string; label: string }[];
  lesson_plans_virtual?: boolean;
  times_virtual?: boolean;
  instructor_ids?: { id: string; label: string }[];
  trainee_ids?: { id: string; label: string }[];
  observer_ids?: { id: string; label: string }[];
  destroy: string;
}

interface FileMetadata {
  title: string;
}

export interface FormValues {
  id?: number;
  title: string;
  is_online: string;
  video_url?: string;
  payment_type: string;
  invoice_type?: string;
  price_per_class: string;
  can_be_prorated?: boolean;
  course_type: string;
  theme_id: string;
  status: string;
  venue_id: { value?: number; label: string };
  venue_timezone?: string;
  is_hidden: boolean;
  opts_out_of_roster_emails: boolean;
  room?: string;
  capacity: number;
  instructor_ids: { id: string; label: string }[];
  min_instructors: number;
  instructor_notes?: string;
  pay_type: string;
  flat_rate?: string;
  admin_notes: string;
  description: string;
  registered_information: string;
  day_of_week: string[];
  starts_at: string;
  ends_at: string;
  starts_on?: string;
  ends_on?: string;
  no_class?: string[];
  registration_starts_on?: string;
  registration_starts_at?: string;
  registration_ends_on?: string;
  registration_ends_at?: string;
  registration_window?: string;
  classes: ClassValues[];
  document_ids?: { value: string | number; label: string }[];
  files?: File[];
  file_metadata?: FileMetadata[];
}

export interface StepProps {
  errors: Partial<FormikErrors<FormValues>>;
  touched: Partial<FormikTouched<FormValues>>;
  values?: FormValues;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  setFieldTouched?: (field: string, touched?: boolean, shouldValidate?: boolean) => void;
  readOnly?: boolean;
}

/** Normalize lesson/class dates to YYYY-MM-DD for consistent map lookups. */
function normalizeClassDate(date?: string | null): string {
  return toDateOnly(date) || (date?.trim() ?? '');
}

const FORM_DAY_ORDER_LIST = [...FORM_DAY_ORDER];

/** Derive schedule day abbreviations from class dates (calendar dates, timezone-neutral). */
function deriveDayOfWeekFromDates(dates: string[]): string[] {
  const abbreviations = new Set<string>();

  for (const date of dates) {
    const normalized = normalizeClassDate(date);
    if (!normalized || !isValidISODate(normalized)) continue;

    const [year, month, day] = normalized.split('-').map(Number);
    const dayIndex = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
    abbreviations.add(FORM_DAY_ORDER_LIST[dayIndex]);
  }

  return FORM_DAY_ORDER_LIST.filter((day) => abbreviations.has(day));
}

/**
 * Dates that fall on the recurring schedule but have no lesson — e.g. after a class
 * card was deleted without adding an explicit no_class entry.
 */
function inferNoClassDatesFromScheduleGaps(
  lessonDates: string[],
  dayOfWeek: string[],
  startsOn: string,
  endsOn: string,
  existingNoClass: string[]
): string[] {
  if (
    !dayOfWeek.length ||
    !startsOn ||
    !endsOn ||
    !isValidISODate(startsOn) ||
    !isValidISODate(endsOn)
  ) {
    return existingNoClass;
  }

  const lessonSet = new Set(lessonDates);
  const noClassSet = new Set(existingNoClass.map(normalizeClassDate).filter(Boolean));
  const inferredGaps: string[] = [];

  // Iterate calendar dates with NO timezone conversion. dateFromString /
  // formatDate (account TZ) shift the weekday in US timezones.
  const scheduleEnd = parse(endsOn, 'yyyy-MM-dd', new Date());
  let cursor       = parse(startsOn, 'yyyy-MM-dd', new Date());
  let iterations = 0;

  while (cursor <= scheduleEnd && iterations < 365) {
    iterations++;
    const ds = format(cursor, 'yyyy-MM-dd');
    const dayAbbr = format(cursor, 'EEEE').slice(0, 3);

    if (dayOfWeek.includes(dayAbbr) && !lessonSet.has(ds) && !noClassSet.has(ds)) {
      inferredGaps.push(ds);
    }
    cursor = addDays(cursor, 1);
  }

  return [...existingNoClass, ...inferredGaps].sort();
}

/**
 * Resolve schedule bounds for class reconciliation. Prefer form values; fall back to
 * the span of existing classes/lessons when form bounds are missing or invalid.
 */
function resolveScheduleDateBounds(
  formValues: FormValues,
  serverLessons: { date: string }[] = []
): { starts_on: string; ends_on: string } {
  const formStart = normalizeClassDate(formValues.starts_on);
  const formEnd = normalizeClassDate(formValues.ends_on);
  const classDates = formValues.classes
    .filter((c) => String(c.destroy) !== 'true')
    .map((c) => normalizeClassDate(c.date))
    .filter(Boolean);
  const serverDates = serverLessons.map((l) => normalizeClassDate(l.date)).filter(Boolean);
  const allDates = [...classDates, ...serverDates].filter(Boolean).sort();

  if (formStart && formEnd && isValidISODate(formStart) && isValidISODate(formEnd)) {
    return { starts_on: formStart, ends_on: formEnd };
  }

  return {
    starts_on: allDates[0] ?? formStart,
    ends_on: allDates.at(-1) ?? formEnd
  };
}

/** Location timezone for the selected venue, via existing location lookup (same as LocationDetails). */
async function fetchLocationTimezone(
  locationId: number,
  dispatch: ReturnType<typeof useAppDispatch>
): Promise<string | undefined> {
  try {
    const { location } = await dispatch(
      locationsApi.endpoints.getLocation.initiate(locationId)
    ).unwrap();
    return location?.timezone ?? undefined;
  } catch {
    return undefined;
  }
}

function combineRegDateTime(datePart: string, timePart?: string | null): Date {
  const [y, mo, d] = datePart.split('-').map(Number);
  const mins = timeStringToMinutes(timePart ?? '12:00am') ?? 0;
  const dt = new Date(y, mo - 1, d);
  dt.setHours(Math.floor(mins / 60), mins % 60, 0, 0);
  return dt;
}

const validationSchemas = [
  Yup.object({
    title: Yup.string().required('Title is required'),
    is_online: Yup.string().required(),
    video_url: Yup.string().when('is_online', (is_online, schema) => {
      if (is_online[0] === 'online') {
        return schema.required('Online class link is required');
      }
      return schema;
    }),
    payment_type: Yup.string().required(),
    invoice_type: Yup.string().when('payment_type', (payment_type, schema) => {
      if (payment_type[0] === 'onsite') {
        return schema.required('Program type is required');
      }
      return schema;
    }),
    price_per_class: Yup.string().required('Tuition price is required'),
    can_be_prorated: Yup.boolean().when('payment_type', (payment_type, schema) => {
      if (payment_type[0] === 'website') {
        return schema.required();
      }
      return schema;
    }),
    course_type: Yup.string().required(),
    theme_id: Yup.string().required('Theme selection is required'),
    status: Yup.string().required(),
    is_hidden: Yup.boolean().required(),
    opts_out_of_roster_emails: Yup.boolean().required()
  }),
  Yup.object({
    venue_id: Yup.object({
      value: Yup.number().required('Venue selection is required'),
      label: Yup.string().optional()
    }).required(),
    room: Yup.string()
      .nullable()
      .transform((value) => (value === '' ? null : value))
      .optional(),
    capacity: Yup.number()
      .required('Class capacity is required')
      .integer('Capacity must be a whole number')
      .min(0, 'Capacity must be 0 or greater')
  }),
  Yup.object({
    min_instructors: Yup.number()
      .required('Number of instructors is required')
      .positive('Value must be a positive number'),
    instructor_ids: Yup.array()
      .max(Yup.ref('min_instructors'), 'Main instructors selected exceeds limit above')
      .optional(),
    pay_type: Yup.string().required('Pay type is required'),
    flat_rate: Yup.number().when('pay_type', (pay_type, schema) => {
      if (pay_type[0] === 'flat_rate') {
        return schema.required('Flat Daily Rate is required');
      }
      return schema;
    }),
    instructor_notes: Yup.string()
      .transform((value) => (!value || value === '' ? null : value))
      .nullable()
  }),
  Yup.object({
    admin_notes: Yup.string().optional(),
    description: Yup.string().optional(),
    registered_information: Yup.string().optional()
  }),
  Yup.object({
    day_of_week: Yup.array().min(1, 'Select at least one day'),
    starts_at: Yup.string().required('Start time is required'),
    ends_at: Yup.string()
      .required('End time is required')
      .test('is-after-start', 'End Time must be later than Start Time', function (endTime) {
        const startTime = this.parent.starts_at;

        const start = timeStringToMinutes(startTime);
        const end = timeStringToMinutes(endTime);

        // Let required() handle empty strings
        if (start === null || end === null) return true;

        return end > start; // ❗ main rule
      }),
      // Dates are derived server-side from the class dates (validateCourseDates.ts
      // then course.ts recompute min/max over lessons on every save), so classes
      // outside the entered range are no longer an error. Only reject a value that
      // is not a real calendar date.
      starts_on: Yup.string()
          .nullable()
          .test('valid-start-date', 'Start Date is not a valid calendar date', (value) =>
              !value ? true : isValidISODate(value)
          ),
      ends_on: Yup.string()
          .nullable()
          .test('valid-end-date', 'End Date is not a valid calendar date', (value) =>
              !value ? true : isValidISODate(value)
          ),

    no_class: Yup.array()
      .of(Yup.string())
      .test('valid-no-class-dates', 'One or more No Class dates are invalid', (noClassDates) => {
        if (!noClassDates) return true;
        return noClassDates.every((d) => !d || isValidISODate(d));
      })
      .test(
        'no-class-in-range',
        'No Class dates must fall between Start Date and End Date',
        function (noClassDates) {
          const { starts_on, ends_on } = this.parent;
          if (!isValidISODate(starts_on) || !isValidISODate(ends_on) || !noClassDates) return true;

          const start = new Date(starts_on);
          const end = new Date(ends_on);

          return noClassDates.every((d) => {
            if (!d || !isValidISODate(d)) return true;
            const date = new Date(d);
            return date > start && date < end;
          });
        }
      ),
    registration_starts_on: Yup.string().nullable(),
    registration_starts_at: Yup.string().nullable(),
    registration_ends_on: Yup.string().nullable(),
    registration_ends_at: Yup.string().nullable()
  }).test('registration-start-before-end', function (values) {
    if (!values) return true;

    const {
      registration_starts_on,
      registration_starts_at,
      registration_ends_on,
      registration_ends_at
    } = values;

    // Only validate if both start and end are provided
    if (!registration_starts_on?.trim() || !registration_ends_on?.trim()) return true;

    const start = combineRegDateTime(registration_starts_on, registration_starts_at);
    const end = combineRegDateTime(registration_ends_on, registration_ends_at);

    if (start >= end) {
      const endDateBeforeStart = registration_ends_on!.trim() < registration_starts_on!.trim();

      return this.createError({
        path: endDateBeforeStart ? 'registration_ends_on' : 'registration_ends_at',
        message: 'Registration end must be after registration start.'
      });
    }

    return true;
  }),
  Yup.object({
    classes: Yup.array()
      .of(
        Yup.object().shape({
          date: Yup.string().required('Date is required'),
          starts_at: Yup.string().required('Start time is required'),
          ends_at: Yup.string().required('End time is required'),
          notes: Yup.string().optional(),
          lesson_plan_ids: Yup.array(),
          instructor_ids: Yup.array(),
          trainee_ids: Yup.array(),
          observer_ids: Yup.array()
        })
      )
      .min(1, 'Add at least one class')
  }),
  Yup.object({
    document_ids: Yup.array().optional(),
    files: Yup.array().optional(),
    file_metadata: Yup.array()
  })
];

interface AddEditSessionProps {
  editing?: boolean;
}

export const AddEditSession: React.FC<AddEditSessionProps> = ({ editing }) => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(() => {
    if (!editing || searchParams.get('step') !== 'instructor') return 0;
    const instructorStep = steps.findIndex((s) => s.name === 'Instructor');
    return instructorStep >= 0 ? instructorStep : 0;
  });
  const [createCourse] = useCreateCourseMutation();
  const [updateCourse] = useUpdateCourseMutation();
  const { data, error, isLoading } = useGetCourseQuery(Number(params.id!), {
    skip: !editing || !params.id
  });
  const { data: venuesData } = useGetVenuesQuery({
    scope: 'active',
    data: { entries: 10000, orderBy: { field: 'title', order: 'asc' } }
  });
  const dispatch = useAppDispatch();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  const [initialValues, setInitialValues] = useState<FormValues>({
    title: '',
    is_online: 'in_person',
    payment_type: 'website',
    price_per_class: '',
    can_be_prorated: false,
    course_type: '',
    theme_id: '',
    status: 'open',
    venue_id: { label: '' },
    is_hidden: false,
    opts_out_of_roster_emails: false,
    room: '',
    capacity: 0,
    instructor_ids: [],
    min_instructors: 1,
    instructor_notes: '',
    admin_notes: '',
    description: '',
    registered_information: '',
    pay_type: '',
    day_of_week: [],
    starts_at: '12:00',
    ends_at: '13:00',
    starts_on: '',
    ends_on: '',
    registration_starts_on: '',
    registration_starts_at: '',
    registration_ends_on: '',
    registration_ends_at: '',
    classes: [],
    document_ids: [],
    files: [],
    file_metadata: []
  });

  useEffect(() => {
    if (editing && error && !isLoading) {
      addToast('Error while loading session', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, error, isLoading]);

  useEffect(() => {
    if (data && data.course) {
      const { course } = data;

      // Guard: courses with no schedule data cannot be meaningfully edited
      if (
        (!course.starts_at && !course.starts_on && !course.lessons.length)
      ) {
        addToast('This course has incomplete data and cannot be edited.', 'error');
        navigate(`/admin/schedule/sessions/${course.id}`);
        return;
      }

      const instructor_ids = course.course_assignments.map((item) => ({
        id: String(item.instructor_id),
        label: `${item.instructor?.first_name} ${item.instructor?.last_name}`
      }));
      const lessonDates = course.lessons
        .map((lesson) => normalizeClassDate(lesson.date))
        .filter(Boolean)
        .sort();
      const day_of_week = (() => {
        const fromLessons = deriveDayOfWeekFromDates(lessonDates);
        if (fromLessons.length) return fromLessons;
        return parseStoredDayOfWeek(course.day_of_week ?? '');
      })();
      const venueTz = course.venue?.location?.timezone ?? undefined;

      const startDate =
        course.starts_on && course.starts_at
          ? dateFromString(
              `${course.lessons[0]?.date ?? course.starts_on} ${course.starts_at}`,
              'yyyy-MM-dd h:mm a'
            )
          : null;
      const endDate =
        course.ends_on && course.ends_at
          ? dateFromString(
              `${course.lessons.at(-1)?.date ?? course.ends_on} ${course.ends_at}`,
              'yyyy-MM-dd h:mm a'
            )
          : null;
      const defaultStartsAt = sessionTimeToFormHhmm(course.starts_at ?? '', venueTz);
      const defaultEndsAt = sessionTimeToFormHhmm(course.ends_at ?? '', venueTz);
      const classes: ClassValues[] = course.lessons.map((lesson) => {
        const startsAtRaw = isMissingSessionTime(lesson.starts_at)
          ? (course.starts_at ?? '')
          : (lesson.starts_at ?? course.starts_at ?? '');
        const endsAtRaw = isMissingSessionTime(lesson.ends_at)
          ? (course.ends_at ?? '')
          : (lesson.ends_at ?? course.ends_at ?? '');

        return {
          id: lesson.id,
          date: normalizeClassDate(lesson.date),
          times_virtual:
              isMissingSessionTime(lesson.starts_at) && isMissingSessionTime(lesson.ends_at),
          starts_at: sessionTimeToFormHhmm(startsAtRaw, venueTz) || defaultStartsAt,
          ends_at: sessionTimeToFormHhmm(endsAtRaw, venueTz) || defaultEndsAt,
          notes: lesson.notes && lesson.notes.trim() !== '' ? lesson.notes : '',
          lesson_plans_virtual: (lesson as any).lesson_plans_virtual === true,
          lesson_plan_ids: lesson.lesson_plans.map((plan) => ({
            id: String(plan.id),
            label: plan.name
          })),
          instructor_ids: lesson.lesson_assignments
            .filter((item) => item.instructor_role === 'instructor' && item.instructor_id != null)
            .map((assignemnt) => ({
              id: String(assignemnt.instructor_id),
              label: `${assignemnt.instructor?.first_name} ${assignemnt.instructor?.last_name}`
            })),
          trainee_ids: lesson.lesson_assignments
            .filter((item) => item.instructor_role === 'trainee' && item.instructor_id != null)
            .map((assignemnt) => ({
              id: String(assignemnt.instructor_id),
              label: `${assignemnt.instructor?.first_name} ${assignemnt.instructor?.last_name}`
            })),
          observer_ids: lesson.lesson_assignments
            .filter((item) => item.instructor_role === 'observer' && item.instructor_id != null)
            .map((assignemnt) => ({
              id: String(assignemnt.instructor_id),
              label: `${assignemnt.instructor?.first_name} ${assignemnt.instructor?.last_name}`
            })),
          destroy: 'false'
        };
      });
      const document_ids = course.documents.map((doc) => ({
        value: doc.id,
        label: doc.title
      }));
      const resolvedStartsOn =
        normalizeClassDate(course.starts_on) ||
        (startDate && !isNaN(startDate.getTime()) ? formatDate(startDate, 'yyyy-MM-dd') : '') ||
        lessonDates[0] ||
        '';
      const resolvedEndsOn =
        normalizeClassDate(course.ends_on) ||
        (endDate && !isNaN(endDate.getTime()) ? formatDate(endDate, 'yyyy-MM-dd') : '') ||
        lessonDates.at(-1) ||
        '';
      const existingNoClass = Array.isArray(course.no_class)
        ? course.no_class
        : typeof course.no_class === 'string' && course.no_class.trim() !== ''
          ? (course.no_class as string)
              .split(',')
              .map((d: string) => d.trim())
              .filter(Boolean)
          : [];
      // No inference on load. A gap between classes is just a gap, and the
      // classes are the source of truth. Inferring here made the dates part of
      // form state, so they were saved back and could never be removed: delete
      // them, save, and the same gaps regenerate them on the next load.
      const no_class = existingNoClass;
      setInitialValues({
        id: course.id,
        title: course.title,
        is_online: course.is_online ? 'online' : 'in_person',
        ...(course.is_online && { video_url: course.video_url }),
        payment_type: course.payment_type,
        ...(course.payment_type === 'onsite' && { invoice_type: course.invoice_type }),
        price_per_class: String(course.price_per_class),
        ...(course.payment_type === 'website' && { can_be_prorated: course.can_be_prorated }),
        course_type: course.course_type,
        theme_id: String(course.theme_id),
        status: course.status,
        venue_id: {
          value: course.venue_id,
          label: course.venue?.title ? `${course.venue?.title}` : ''
        },
        venue_timezone: course.venue?.location?.timezone,
        is_hidden: course.is_hidden,
        opts_out_of_roster_emails: course.opts_out_of_roster_emails,
        ...(course.room !== '' && { room: course.room }),
        capacity: course.capacity,
        instructor_ids,
        min_instructors: course.min_instructors,
        ...(course.instructor_notes &&
          course.instructor_notes !== '' && { instructor_notes: course.instructor_notes }),
        admin_notes: course.admin_notes,
        description: course.description,
        registered_information: course.registered_information,
        pay_type: course.pay_type,
        ...(course.pay_type === 'flat_rate' && { flat_rate: course.flat_rate }),
        day_of_week,
        starts_at: defaultStartsAt,
        ends_at: defaultEndsAt,
        starts_on: resolvedStartsOn,
        ends_on: resolvedEndsOn,
        ...(toDateOnly(course.registration_starts_on) && {
          registration_starts_on: toDateOnly(course.registration_starts_on),
          registration_starts_at: sessionTimeToFormHhmm(
            course.registration_starts_at ?? '',
            venueTz
          )
        }),
        ...(toDateOnly(course.registration_ends_on) && {
          registration_ends_on: toDateOnly(course.registration_ends_on),
          registration_ends_at: sessionTimeToFormHhmm(course.registration_ends_at ?? '', venueTz)
        }),
        classes: classes,
        no_class,
        document_ids,
        files: [],
        file_metadata: []
      });
    }

    if (!userCanEditCreateDelete_Sessions(currentUser)) {
      addToast('You have no permissions to edit this data', 'error');
      navigate(`/admin/schedule/sessions`);
    }
  }, [data]);

  const isEditDataLoading = editing && (isLoading || initialValues.id !== Number(params.id));

  const handleRegister = async (formValues: FormValues) => {
    const selectedVenue = (venuesData?.results as Venue[] | undefined)?.find(
      (v) => v.id === formValues.venue_id?.value
    );
    // Active-scope list may omit an inactive venue still selected on edit; fall back to course venue.
    const locationId =
      selectedVenue?.location_id ??
      selectedVenue?.location?.id ??
      data?.course?.venue?.location_id ??
      data?.course?.venue?.location?.id;
    let venueTz = locationId ? await fetchLocationTimezone(locationId, dispatch) : undefined;
    if (!venueTz) {
      venueTz = data?.course?.venue?.location?.timezone ?? undefined;
    }
    const toStoredAmPm = (formHhmm: string) =>
      venueTz ? formHhmmToStoredSessionAmPm(formHhmm, venueTz) : hhmmToAmPm(formHhmm);

    const formclasses = formValues.classes;
    const classes = formclasses
      .map((value) => {
        return {
          ...value,
          // Keep the calendar date as-is; do not re-derive from time (empty/invalid times
          // would corrupt dates and collapse distinct classes onto one day on save).
          date: normalizeClassDate(value?.date),
          // Send null when the card still shows the session default. The lesson
          // then inherits from the course (v2 lesson.rb:213 parity), so changing
          // the session time propagates instead of leaving the cards stale.
          starts_at: value.times_virtual ? null : toStoredAmPm(value.starts_at),
          ends_at: value.times_virtual ? null : toStoredAmPm(value.ends_at),
          notes: value?.notes && value?.notes !== '' ? value?.notes : ' ',
          lesson_plan_ids:
              value.lesson_plan_ids && value.lesson_plan_ids.length > 0 && !value.lesson_plans_virtual
                  ? value.lesson_plan_ids?.map((l) => l.id)
                  : undefined,
          instructor_ids:
            value.instructor_ids && value.instructor_ids.length > 0
              ? value.instructor_ids.map((i) => i.id)
              : undefined,
          trainee_ids:
            value.trainee_ids && value.trainee_ids.length > 0
              ? value.trainee_ids.map((i) => i.id)
              : undefined,
          observer_ids:
            value.observer_ids && value.observer_ids.length > 0
              ? value.observer_ids.map((i) => i.id)
              : undefined
        };
      })
      .sort((a, b) => a.date.localeCompare(b.date));

    const isDestroyed = (d: string | boolean | undefined) => String(d) === 'true';

    // Only mark a date as no-class when every class row on that date was removed.
    const destroyedCountByDate = new Map<string, { total: number; destroyed: number }>();
    for (const classRow of classes) {
      const date = normalizeClassDate(classRow.date);
      if (!date) continue;
      const stats = destroyedCountByDate.get(date) ?? { total: 0, destroyed: 0 };
      stats.total += 1;
      if (isDestroyed(classRow.destroy)) stats.destroyed += 1;
      destroyedCountByDate.set(date, stats);
    }
    const userDestroyedDates = [...destroyedCountByDate.entries()]
      .filter(([, stats]) => stats.total > 0 && stats.total === stats.destroyed)
      .map(([date]) => date);
    const resolvedNoClass = [
      ...new Set([...(formValues.no_class ?? []), ...userDestroyedDates])
    ].sort();

    // Reconcile classes against the current schedule so the correct set is
    // submitted even if the user skipped the Classes step.
    const reconciledClasses: typeof classes = [];
    const serverLessons = editing ? (data?.course.lessons ?? []) : [];
    const scheduleBounds = resolveScheduleDateBounds(formValues, serverLessons);
    if (
        !editing &&
        scheduleBounds.starts_on &&
        scheduleBounds.ends_on &&
        isValidISODate(scheduleBounds.starts_on) &&
        isValidISODate(scheduleBounds.ends_on) &&
        formValues.day_of_week?.length
    ) {
      // New session only: generate any recurrence dates the user did not already
      // add in the Classes step (covers the case where they skipped that step).
      // On edit, the user's class rows are authoritative and we never regenerate
      // (see the else branch) - that is what prevents date snaps and recipe wipes.
      const expectedDates = new Set<string>();
      const scheduleEnd = parse(scheduleBounds.ends_on, 'yyyy-MM-dd', new Date());
      let cursor       = parse(scheduleBounds.starts_on, 'yyyy-MM-dd', new Date());
      let iterations = 0;
      // Calendar dates only — do not use formatDate (account TZ); see ClassesForm.
      while (cursor <= scheduleEnd && iterations < 365) {
        iterations++;
        const ds = format(cursor, 'yyyy-MM-dd');
        const dayAbbr = format(cursor, 'EEEE').slice(0, 3);
        if (formValues.day_of_week.includes(dayAbbr) && !resolvedNoClass.includes(ds)) {
          expectedDates.add(ds);
        }
        cursor = addDays(cursor, 1);
      }

      const coveredDates = new Set<string>();

      // Submit every user class row as-is.
      for (const classRow of classes) {
        const date = normalizeClassDate(classRow.date);
        reconciledClasses.push({
          ...classRow,
          date,
          notes: classRow.notes && classRow.notes !== '' ? classRow.notes : ' '
        });
        if (!isDestroyed(classRow.destroy)) coveredDates.add(date);
      }

      // Fill recurrence dates the user has no class for with fresh blank classes.
      for (const ds of expectedDates) {
        if (coveredDates.has(ds)) continue;
        reconciledClasses.push({
          date: ds,
          starts_at: null,
          ends_at: null,
          times_virtual: true,
          notes: ' ',
          destroy: 'false',
          lesson_plan_ids: undefined,
          instructor_ids: undefined,
          trainee_ids: undefined,
          observer_ids: undefined
        });
        coveredDates.add(ds);
      }
    } else {
      reconciledClasses.push(...classes);
    }

    const {
      registration_starts_on,
      registration_starts_at,
      registration_ends_on,
      registration_ends_at
    } = formValues;

    const activeClasses = reconciledClasses.filter((c) => String(c.destroy) !== 'true');
    if (!activeClasses.length) {
      addToast('Add at least one class before saving the session.', 'error');
      return;
    }

    const normalizedRegStart = toDateOnly(registration_starts_on);
    const normalizedRegEnd = toDateOnly(registration_ends_on);
    const hasStartDate = Boolean(normalizedRegStart);
    const hasEndDate = Boolean(normalizedRegEnd);

    const resolvedRegistration = {
      registration_starts_on: hasStartDate ? normalizedRegStart : null,
      registration_starts_at: hasStartDate
        ? registration_starts_at
          ? toStoredAmPm(registration_starts_at)
          : null
        : null,
      // Blank stays blank: the API leaves it null and every consumer derives
      // "closes with the last class" at read time, so the window tracks classes
      // added later instead of freezing at whatever the last class was on save.
      registration_ends_on: hasEndDate ? normalizedRegEnd : null,
      registration_ends_at: hasEndDate
          ? registration_ends_at
              ? toStoredAmPm(registration_ends_at)
              : null
          : null
    };

    // Create FormData object
    const formData = new FormData();

    // Add all non-file fields to FormData
    formData.append('title', formValues.title);
    formData.append('is_online', formValues.is_online === 'online' ? 'true' : 'false');
    formData.append('payment_type', formValues.payment_type);
    formData.append('price_per_class', formValues.price_per_class);
    formData.append('course_type', formValues.course_type);
    formData.append('theme_id', formValues.theme_id);
    formData.append('status', formValues.status);
    formData.append('is_hidden', String(formValues.is_hidden));
    formData.append('opts_out_of_roster_emails', String(formValues.opts_out_of_roster_emails));
    formData.append('capacity', String(formValues.capacity));
    formData.append('min_instructors', String(formValues.min_instructors));
    formData.append(
      'day_of_week',
      convertDaysToFull(formValues.day_of_week, formValues.starts_on!, formValues.ends_on!)
    );
    formData.append('starts_at', toStoredAmPm(formValues.starts_at));
    formData.append('ends_at', toStoredAmPm(formValues.ends_at));
    // `starts_on` and `ends_on` are calculated server-side for FormData submissions
    // (for the JSON path we deleted these fields before sending). Do not send them
    // as part of the multipart payload to avoid backend validation errors.
    formData.append('pay_type', formValues.pay_type);
    formData.append('admin_notes', formValues.admin_notes);
    formData.append('description', formValues.description);
    formData.append('registered_information', formValues.registered_information);
    formData.append('venue_id', String(formValues.venue_id.value) ?? '');

    // Add conditional fields
    if (formValues.is_online === 'online' && formValues.video_url) {
      formData.append('video_url', formValues.video_url);
    }
    if (formValues.payment_type === 'onsite' && formValues.invoice_type) {
      formData.append('invoice_type', formValues.invoice_type);
    }
    if (formValues.payment_type === 'website') {
      formData.append('can_be_prorated', String(formValues.can_be_prorated));
    }
    if (
      formValues.pay_type === 'flat_rate' &&
      formValues.flat_rate != null &&
      formValues.flat_rate !== ''
    ) {
      formData.append('flat_rate', String(formValues.flat_rate));
    }
    formData.append('room', formValues.room ?? '');
    if (formValues.instructor_notes && formValues.instructor_notes !== '') {
      formData.append('instructor_notes', formValues.instructor_notes);
    }
    formData.append('no_class', resolvedNoClass.join(', '));
    formData.append('registration_starts_on', resolvedRegistration.registration_starts_on ?? '');
    formData.append('registration_ends_on', resolvedRegistration.registration_ends_on ?? '');
    if (resolvedRegistration.registration_starts_on) {
        formData.append('registration_starts_at', resolvedRegistration.registration_starts_at ?? '');    }
    if (resolvedRegistration.registration_ends_on) {
        formData.append('registration_ends_at', resolvedRegistration.registration_ends_at ?? '');
    }

    // Add arrays and objects
    formValues.instructor_ids.forEach((instructor, index) => {
      formData.append(`instructor_ids[${index}]`, instructor.id);
    });

    if (formValues.document_ids && formValues.document_ids.length > 0) {
      formValues.document_ids.forEach((doc, index) => {
        formData.append(`document_ids[${index}]`, String(doc.value));
      });
    }

    // Add files
    if (formValues.files && formValues.files.length > 0) {
      formValues.files.forEach((file, index) => {
        formData.append(`files`, file);
        if (formValues.file_metadata && formValues.file_metadata[index]) {
          formData.append(`file_metadata[${index}][title]`, formValues.file_metadata[index].title);
        }
      });
    }

    // Add classes
    reconciledClasses.forEach((classItem, index) => {
      if (classItem.id) {
        formData.append(`classes[${index}][id]`, String(classItem.id));
      }
      formData.append(`classes[${index}][date]`, classItem.date);
      formData.append(`classes[${index}][starts_at]`, classItem.starts_at ?? '');
      formData.append(`classes[${index}][ends_at]`, classItem.ends_at ?? '');
      formData.append(`classes[${index}][notes]`, classItem.notes || ' ');
      formData.append(`classes[${index}][destroy]`, classItem.destroy || 'false');

      if (classItem.lesson_plan_ids) {
        classItem.lesson_plan_ids.forEach((planId, planIndex) => {
          formData.append(`classes[${index}][lesson_plan_ids][${planIndex}]`, planId);
        });
      }

      if (classItem.instructor_ids) {
        classItem.instructor_ids.forEach((instructorId, instIndex) => {
          formData.append(`classes[${index}][instructor_ids][${instIndex}]`, instructorId);
        });
      }

      if (classItem.trainee_ids) {
        classItem.trainee_ids.forEach((traineeId, traineeIndex) => {
          formData.append(`classes[${index}][trainee_ids][${traineeIndex}]`, traineeId);
        });
      }

      if (classItem.observer_ids) {
        classItem.observer_ids.forEach((observerId, observerIndex) => {
          formData.append(`classes[${index}][observer_ids][${observerIndex}]`, observerId);
        });
      }
    });

    // Editing or creating
    if (editing && data?.course.id) {
      formData.append('id', String(data.course.id));
    }

    try {
      if (editing) {
        const res = await updateCourse(formData).unwrap();
        if (res && res.course) {
          navigate(`/admin/schedule/sessions/${res.course.id}`);
          addToast('Session updated successfully', 'success');
        }
      } else {
        const res = await createCourse(formData).unwrap();
        if (res && res.course) {
          navigate(`/admin/schedule/sessions/${res.course.id}`);
          addToast('Session created successfully', 'success');
          return;
        } else throw new Error('A problem happened while creating session');
      }
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  const handleNext = async (
    values: FormValues,
    setTouched: (touched: FormikTouched<FormValues>, shouldValidate?: boolean) => void
  ) => {
    try {
      // Validate only the current step
      await validationSchemas[currentStep].validate(values, { abortEarly: false });

      // If valid, go forward
      setCurrentStep((prev) => prev + 1);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        // Mark ONLY current-step fields as touched so errors show

        if (currentStep === 1) {
          // Venue page (Page 2)
          setTouched(
            {
              venue_id: { value: true },
              capacity: true
            } as FormikTouched<FormValues>
            // (optional) , true
          );
          return;
        }

        // Generic fallback: show errors for whatever step you're on
        const touchedFields: FormikTouched<FormValues> = {};
        err.inner.forEach((e) => {
          if (!e.path) return;
          const [parent, child] = e.path.split('.');
          if (child) {
            touchedFields[parent as keyof FormValues] = { [child]: true } as any;
          } else {
            touchedFields[parent as keyof FormValues] = true as any;
          }
        });

        setTouched(touchedFields /*, true */);
      }
    }
  };

  return (
    <FadeIn className="p-lg-4">
      <Stack
        gap={3}
        className="py-lg-0 px-4 py-2"
      >
        <div>
          <BackButton
            text={`Back to session${editing ? ' details' : 's'}`}
            color="#2B4E64"
            url={
              editing ? `/admin/schedule/sessions/${data?.course.id}` : '/admin/schedule/sessions'
            }
          />
          <h2
            className="d-none d-lg-block text-info fw-bold mb-0"
            style={{ fontSize: '2.25rem' }}
          >
            {editing ? 'Edit' : 'New'} Session
          </h2>
          <Stepper
            steps={steps}
            currentStep={currentStep}
            editing={editing === true}
            onStepClick={(step) => setCurrentStep(step)}
          />
        </div>

        <div
          className="p-lg-5 p-3"
          style={{
            boxShadow: '0 4px 8px -2px rgba(16, 24, 40, .1),  0 4px 4px 0 rgba(0, 0, 0, .25)',
            border: '1px solid #EBEBEB',
            borderRadius: 10
          }}
        >
          {isEditDataLoading ? (
            <div
              className="w-100 d-inline-flex"
              style={{ minHeight: '65vh' }}
            >
              <Spinner
                variant="primary"
                className="m-auto"
              />
            </div>
          ) : (
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchemas[currentStep]}
              onSubmit={handleRegister}
              enableReinitialize
            >
              {({
                submitForm,
                dirty,
                isSubmitting,
                touched,
                errors,
                setTouched,
                setFieldTouched,
                values,
                setFieldValue
              }: FormikProps<FormValues>) => {
                return (
                  <FormikForm className="text-start">
                    <div style={{ minHeight: '65vh' }}>
                      {currentStep < steps.length &&
                        React.createElement(steps[currentStep]?.form, {
                          errors,
                          touched,
                          values,
                          setFieldValue,
                          setFieldTouched
                        })}
                    </div>

                    <Row className="justify-content-end g-2 mt-3">
                      <Col
                        xs={6}
                        lg={3}
                      >
                        {currentStep !== 0 && (
                          <SimpleButton
                            disabled={isSubmitting}
                            type="button"
                            onClick={() => setCurrentStep(currentStep - 1)}
                            className="w-100"
                            variant="outline-primary"
                          >
                            Back
                          </SimpleButton>
                        )}
                      </Col>

                      <Col
                        xs={6}
                        lg={3}
                      >
                        <SimpleButton
                          disabled={isSubmitting}
                          type="button"
                          onClick={
                            currentStep === steps.length - 1
                                ? submitForm
                                : () => handleNext(values, setTouched)
                          }
                          className="w-100"
                          variant="primary"
                        >
                          {currentStep === steps.length - 1 ? 'Finish' : 'Continue'}
                        </SimpleButton>
                      </Col>

                      {editing && currentStep !== steps.length - 1 && (
                        <Col
                          xs={6}
                          lg={3}
                        >
                          <SimpleButton
                            disabled={isSubmitting || !dirty}
                            type="button"
                            onClick={submitForm}
                            className="w-100"
                            variant="outline-primary"
                          >
                            Save & Close
                          </SimpleButton>
                        </Col>
                      )}
                    </Row>
                  </FormikForm>
                );
              }}
            </Formik>
          )}
        </div>
      </Stack>
    </FadeIn>
  );
};
